import { groupBy } from "lodash";
import memoize from "lodash.memoize";
import { OUTER, SECTOR, POSTCODE } from "../consts/layers";
import { getLabelCoordinates, getMinZoomForLabel } from "./map";
import { Postcode } from "./postcode";
import { generateColor } from "./routes";

export const groupPostcodes = memoize((postcodeTable) =>
  postcodeTable
    ? groupBy(Object.keys(postcodeTable), (key) =>
        key.substr(0, key.length - 2)
      )
    : {}
);

export const groupSectors = memoize((sectorTable) =>
  sectorTable
    ? groupBy(Object.keys(sectorTable), (key) =>
        key
          .substr(0, key.length - 1)
          .split(" ")
          .join("")
      )
    : {}
);

export const generateLookupTablesExtras = (lookupTables) =>
  Object.keys(lookupTables).forEach((level) =>
    Object.values(lookupTables[level]).forEach((value) => {
      if (level === OUTER) {
        const isChild = new RegExp(`^${value.postcode} `);
        value.children = Object.keys(lookupTables[SECTOR]).filter(
          isChild.test.bind(isChild)
        ).length;
        value.labelMinZoom = getMinZoomForLabel(value);
      } else if (level === SECTOR) {
        const isChild = new RegExp(`^${value.postcode}`);
        value.children = Object.keys(lookupTables[POSTCODE]).filter(
          isChild.test.bind(isChild)
        ).length;
      }
      if (level !== OUTER) value.parent = value.postcode.slice(0, -2);
      value.labelCoordinates = getLabelCoordinates(value);
    })
  );

export const generateArtificialTables = (lookupTables) => {
  const full = {
    OUTER: [],
    SECTOR: [],
    POSTCODE: [],
  };
  const visible = {
    OUTER: {},
    SECTOR: {},
    POSTCODE: {},
  };
  Object.keys(lookupTables).reduce(
    (acc, level) => {
      Object.values(lookupTables[level]).forEach(
        ({ id, postcode, center }, index) => {
          if (id !== 0) return;
          const artificialPostcode = {
            postcode,
            level,
            lng: null,
            lat: null,
            ...generateColor(index),
          };
          acc.full[level].push(artificialPostcode);
          if (!center) return;
          const [lng, lat] = center;
          acc.visible[level][postcode] = artificialPostcode;
          artificialPostcode.lng = lng;
          artificialPostcode.lat = lat;
        }
      );
      return acc;
    },
    {
      full,
      visible,
    }
  );

  full.POSTCODE = full.POSTCODE.sort(({ postcode: a }, { postcode: b }) =>
    a.localeCompare(b)
  );

  return {
    full,
    visible,
  };
};

export const getSiblingsCount = (postcodes, lookupTables) => {
  if (!postcodes?.length) return 0;
  const level = Postcode.getLevel(postcodes[0]);
  if (level === OUTER) return 0;
  const parents = postcodes.reduce((acc, postcode) => {
    acc.add(lookupTables[level][postcode].parent);
    return acc;
  }, new Set());

  const parentLevel = level === SECTOR ? OUTER : SECTOR;
  return Array.from(parents).reduce(
    (acc, parent) => (acc += lookupTables[parentLevel][parent]?.children || 0),
    0
  );
};
